import {
  techDocsExpandableNavigationAddonModule,
  techDocsLightBoxAddonModule,
  techDocsReportIssueAddonModule,
  techDocsTextSizeAddonModule,
} from '@backstage/plugin-techdocs-module-addons-contrib/alpha';
import { createApp } from '@backstage/frontend-defaults';
import catalogGraphApisFeature from './modules/catalogGraphModule';
import { graphiqlModule } from './modules/graphiqlModule';
import scoreCardPlugin from '@internal/plugin-score-card/alpha';
import adrPlugin from '@internal/plugin-adr/alpha';
import techRadarAoePlugin from '@internal/plugin-tech-radar-aoe/alpha';
import orgDataPlugin from '@internal/plugin-org-data/alpha';
import goldenPathsPlugin from '@internal/plugin-golden-paths/alpha';
import githubExplorerPlugin from '@internal/plugin-github-explorer/alpha';
import infraWalletPlugin from '@internal/plugin-infrawallet/alpha';
import gitrepoDiscoveryPlugin from '@internal/plugin-gitrepo-discovery/alpha';
import customeCatalogImportPlugin from '@internal/plugin-custome-catalog-import/alpha';
import notificationsFeature from './modules/notificationsModule';
import announcementsFeature from './modules/announcementsModule';
import mgaPlugin from '@internal/plugin-mga/alpha';
import myAiToolsFeature from './modules/myAiToolsModule';
import { navModule } from './modules/nav';
import { appRootModule } from './modules/appRootModule';
import { themeModule } from './modules/themeModule';
import alertDisplayModule from './modules/alertDisplayModule';
import { signInModule } from './modules/signInModule';
import { apisModule } from './modules/apisModule';
import { appPagesModule } from './modules/appPagesModule';
import qetaFeature from './modules/qetaModule';
import homeFeature from './modules/homeModule';
import searchFeature from './modules/searchModule';
import devtoolsFeature from './modules/devtoolsModule';
import settingsFeature from './modules/settingsModule';
import apiDocsFeature, { apiDocsPageModule } from './modules/apiDocsModule';
import scaffolderFeature from './modules/scaffolderModule';
import techdocsFeature from './modules/techdocsModule';
import catalogFeature from './modules/catalogModule';
import orgFeature from './modules/orgModule';
import bazaarFeature from './modules/bazaarModule';
import playlistFeature from './modules/playlistModule';
import yamlEditorFeature from './modules/yamlEditorModule';
import entityFeedbackFeature from './modules/entityFeedbackModule';
import githubPullRequestsFeature from './modules/githubPullRequestsModule';
import awsCodeBuildFeature from './modules/awsCodeBuildModule';
import amazonEcsFeature from './modules/amazonEcsModule';
import { entityContentExtensionsModule } from './modules/entityContentExtensionsModule';
import kubernetesPlugin from '@backstage/plugin-kubernetes/alpha';
import githubActionsPlugin from '@backstage-community/plugin-github-actions/alpha';
import githubIssuesPlugin from '@backstage-community/plugin-github-issues/alpha';
import datadogPlugin from '@roadiehq/backstage-plugin-datadog/alpha';
// Cast needed: community alpha plugins were built against an older version of
// @backstage/frontend-plugin-api and are missing the `info` property.
// We disable their auto-registered entity content blueprints to avoid duplicate
// tabs — our custom blueprints in entityContentExtensionsModule handle those tabs.
const communityAlphaPlugins = [
  (kubernetesPlugin as any).withOverrides({
    extensions: [
      // "Kubernetes" tab (path: /kubernetes) duplicates our custom blueprint.
      (kubernetesPlugin as any).getExtension('entity-content:kubernetes/kubernetes')
        ?.override({ disabled: true }),
    ].filter(Boolean),
  }),
  (githubActionsPlugin as any).withOverrides({
    extensions: [
      // "GitHub Actions" tab (path: github-actions) conflicts with our "CI/CD"
      // tab (path: /ci-cd) which handles both GitHub Actions and AWS CodeBuild.
      (githubActionsPlugin as any).getExtension('entity-content:github-actions')
        ?.override({ disabled: true }),
    ].filter(Boolean),
  }),
  (githubIssuesPlugin as any).withOverrides({
    extensions: [
      // "GitHub Issues" tab (path: github-issues) duplicates our /github-issues blueprint.
      (githubIssuesPlugin as any).getExtension('entity-content:github-issues/entity')
        ?.override({ disabled: true }),
    ].filter(Boolean),
  }),
  (datadogPlugin as any).withOverrides({
    extensions: [
      // "Datadog" tab (path: /datadog) duplicates our /datadog blueprint.
      (datadogPlugin as any).getExtension('entity-content:datadog/entity')
        ?.override({ disabled: true }),
    ].filter(Boolean),
  }),
] as any[];

const app = createApp({
  features: [
    themeModule,
    navModule,
    appRootModule,
    alertDisplayModule,
    graphiqlModule,
    notificationsFeature,
    announcementsFeature,
    qetaFeature,
    homeFeature,
    searchFeature,
    devtoolsFeature,
    settingsFeature,
    apiDocsFeature,
    apiDocsPageModule,
    scaffolderFeature,
    techdocsFeature,
    techDocsReportIssueAddonModule,
    techDocsTextSizeAddonModule,
    techDocsLightBoxAddonModule,
    techDocsExpandableNavigationAddonModule,
    catalogFeature,
    orgFeature,
    bazaarFeature,
    playlistFeature,
    catalogGraphApisFeature,
    signInModule,
    apisModule,
    appPagesModule,
    mgaPlugin,
    myAiToolsFeature,
    scoreCardPlugin,
    adrPlugin,
    techRadarAoePlugin,
    orgDataPlugin,
    goldenPathsPlugin,
    githubExplorerPlugin,
    infraWalletPlugin,
    gitrepoDiscoveryPlugin,
    customeCatalogImportPlugin,
    yamlEditorFeature,
    entityFeedbackFeature,
    githubPullRequestsFeature,
    awsCodeBuildFeature,
    amazonEcsFeature,
    entityContentExtensionsModule,
    ...communityAlphaPlugins,
  ],
});

export default app.createRoot();
